import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import HomePage from "./HomePage";
import PrivacyPawMerge from "./PrivacyPawMerge";
import PrivacyAetherDash from "./PrivacyAetherDash";
import PrivacySEAS from "./PrivacySEAS";
import PrivacyPDFTools from "./PrivacyPDFTools";
import TermsPDFTools from "./TermsPDFTools";
import PrivacyRascalCat from "./PrivacyRascalCat";
import TermsRascalCat from "./TermsRascalCat";
import TermsPawMerge from "./TermsPawMerge";
import RascalCatPage from "./RascalCatPage";
import AetherDashPage from "./AetherDashPage";
import PawMergeIdlePage from "./PawMergeIdlePage";

const services = [
  {
    title: "iOS & Android Apps",
    desc: "We design and ship custom mobile apps with thoughtful UX, polished interactions, and clear store launch plans.",
  },
  {
    title: "App Maintenance",
    desc: "Bug fixes, SDK updates, store compliance, and live-ops support to keep apps and games healthy after launch.",
  },
  {
    title: "Mobile Game Development",
    desc: "Gameplay systems, progression loops, monetization support, and launch planning for mobile games.",
  },
];

const studioHighlights = [
  "Family-owned studio combining technical delivery with practical mobile growth experience.",
  "Hands-on work from concept through store launch instead of handing projects off to large teams.",
  "Focused on useful apps and games, maintainable code, and straightforward communication.",
];

const processSteps = [
  {
    title: "Discovery",
    desc: "We define the audience, scope, and release goals before writing production code.",
  },
  {
    title: "Design & Build",
    desc: "We move from UX flows and core loops into implementation with attention to performance, usability, and feel.",
  },
  {
    title: "Launch & Support",
    desc: "For the apps and games we build, we handle store submission, launch support, iteration, and ongoing maintenance.",
  },
];

const projectItems = [
  {
    title: "Paw Merge Idle",
    desc: "A cozy merge-and-idle game where cats, paw merges, and furnished rooms turn quick sessions into long-term progression.",
    details: [
      "Merge paws on a 7×7 board, unlock a roster of cats, and decorate their rooms for passive income boosts.",
      "Built with cloud save, guest and provider sign-in, leaderboards, and store-ready legal pages.",
      "Available on Google Play and the App Store.",
    ],
    status: "Available on Google Play and the App Store.",
    image: "/pmi-feature.jpg",
    imageAlt: "Paw Merge Idle feature graphic",
    imageClassName: "h-24 w-40 rounded-2xl object-cover shadow-lg ring-1 ring-slate-200",
    links: [
      {
        href: "/games/paw-merge-idle",
        label: "View game page",
        variant: "primary",
      },
      {
        href: "https://play.google.com/store/apps/details?id=com.appazzola.pawmergeidle&pcampaignid=web_share",
        img: "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg",
        alt: "Get it on Google Play",
        className: "h-10",
      },
      {
        href: "https://apps.apple.com/us/app/paw-merge-idle/id6761851044",
        img: "https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg",
        alt: "Download on the App Store",
        className: "h-10",
      },
    ],
  },
  {
    title: "Rascal Cat",
    desc: "A playful mobile game built around quick sessions, approachable progression, and a bright cat-led world.",
    details: [
      "Designed as a child-directed experience with privacy, ads, purchases, and account flows handled deliberately.",
      "Built with cloud save, leaderboards, guest play, and store-ready legal pages for launch support.",
      "Available on Google Play and the App Store.",
    ],
    status: "Available on Google Play and the App Store.",
    image: "/rascal-cat-app-icon.png",
    imageAlt: "Rascal Cat app icon",
    links: [
      {
        href: "/games/rascal-cat",
        label: "View game page",
        variant: "primary",
      },
      {
        href: "https://play.google.com/store/apps/details?id=com.appazzola.catchaos&pcampaignid=web_share",
        img: "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg",
        alt: "Get it on Google Play",
        className: "h-10",
      },
      {
        href: "https://apps.apple.com/us/app/rascal-cat/id6761418594",
        img: "https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg",
        alt: "Download on the App Store",
        className: "h-10",
      },
    ],
  },
];

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = (nextState) =>
    setIsMenuOpen((currentState) =>
      typeof nextState === "boolean" ? nextState : !currentState
    );

  const appClasses = "app-shell min-h-screen text-slate-950";

  return (
    <Routes>
      <Route path="/" element={
        <HomePage 
          isMenuOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          scrollY={scrollY}
          appClasses={appClasses}
          services={services}
          studioHighlights={studioHighlights}
          processSteps={processSteps}
          projectItems={projectItems}
        />
      } />
      <Route path="/privacy/aether-dash" element={<PrivacyAetherDash />} />
      <Route path="/privacy/SEAS" element={<PrivacySEAS />} />
      <Route path="/privacy/pdf-toolbox" element={<PrivacyPDFTools />} />
      <Route path="/terms/pdf-toolbox" element={<TermsPDFTools />} />
      <Route path="/games/paw-merge-idle" element={<PawMergeIdlePage />} />
      <Route path="/games/rascal-cat" element={<RascalCatPage />} />
      <Route path="/games/aether-dash" element={<AetherDashPage />} />
      <Route path="/privacy/paw-merge-idle" element={<PrivacyPawMerge />} />
      <Route path="/terms/paw-merge-idle" element={<TermsPawMerge />} />
      <Route path="/privacy/rascal-cat" element={<PrivacyRascalCat />} />
      <Route path="/terms/rascal-cat" element={<TermsRascalCat />} />
    </Routes>
  );
};

export default App;
